'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { useSignOut } from '@shared/auth';
import { Button, Label, RadioGroup, RadioGroupItem } from '@shared/components/shadcn';
import { type SaferGamblingTimeOutPeriod, getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { Spinner } from '@shared/icons';
import { SelfExclusion } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '../../../../../form-controls/Checkbox/Checkbox';
import { FieldMessage } from '../../../../../form-controls/FieldMessage/FieldMessage';
import { toast } from '../../../../../Toast/Toast';
import type { SaferGamblingPeriodRequest } from '../../../types';
import { completeBreakToolSubmission } from '../../../utils/breakToolConfirmation';
import { formatIsoDurationForTracking } from '../../../utils/formatTimeOffTracking';
import type { DurationType } from '../TimeOutSheet';

type DurationStepProps = {
  periods: readonly SaferGamblingTimeOutPeriod[];
  periodLabelKeys: Record<SaferGamblingTimeOutPeriod, string>;
};

function DurationStep({ periods, periodLabelKeys }: DurationStepProps) {
  const router = useRouter();
  const signOut = useSignOut();
  const t = useTranslations('form');
  const step = useTranslations('my_account.safer_gambling.time_limits.time_out_sheet');
  const debug = createDebug({ isEnabled: false });
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();

  const { disableBreakToolConfirmUntilValid } = getSiteConfig().SAFER_GAMBLING;
  const selectedDuration = watch('duration');
  const agreed = watch('agreed');

  const takeBreakMutation = useApiMutation<{ success: boolean; message: string; period: string }, SaferGamblingPeriodRequest>(API_ROUTES.MY_ACCOUNT.TAKE_BREAK.ADD, 'POST', {
    mutationOptions: {
      onSuccess: data => {
        debug.log('Take break request successful:', data);
      },
      onError: error => {
        debug.error('Take break request failed:', error);
        toast(step('error_message'), { type: 'error' });
      },
    },
  });
  const isConfirmDisabled = takeBreakMutation.isPending || (disableBreakToolConfirmUntilValid && (!selectedDuration || !agreed));

  const durations: Array<{ value: DurationType; label: string }> = periods.map(value => ({
    value,
    label: step(periodLabelKeys[value]),
  }));

  const handleConfirm = async () => {
    const isValid = await trigger();
    if (isValid && selectedDuration && agreed) {
      debug.log('Submitting take break request with period:', selectedDuration);
      takeBreakMutation.mutate(
        { period: selectedDuration },
        {
          onSuccess: async () => {
            await completeBreakToolSubmission({
              breakType: 'timeout',
              period: selectedDuration,
              signOut,
              router,
            });
          },
        }
      );
    }
  };

  return (
    <div className="flex flex-col p-6 gap-6" data-testid="time-out-duration-step">
      <div className="flex flex-col gap-2 items-start justify-start w-full">
        <SelfExclusion size="extra-extra-large" className="mb-5" data-testid="self-exclusion-icon" />
        <h4>{step('title')}</h4>
        <p>{step('description')}</p>
        {step('description_2') ? <p>{step('description_2')}</p> : null}
      </div>

      <div className="flex flex-col gap-4 items-start justify-start w-full">
        <RadioGroup
          value={selectedDuration ?? ''}
          onValueChange={value => setValue('duration', value, { shouldValidate: true })}
          className="flex flex-col gap-4 w-full"
          data-track-name="Period"
        >
          {durations.map(duration => (
            <div key={duration.value} className="flex flex-row gap-3 h-10 items-center justify-start w-full">
              <RadioGroupItem
                className="size-7"
                value={duration.value}
                id={`duration-${duration.value}`}
                data-testid={`duration-${duration.value}`}
                {...(selectedDuration === duration.value ? { 'data-track-period': formatIsoDurationForTracking(duration.value) } : {})}
              />
              <Label className="paragraph-explanatory hover:text-inherit" htmlFor={`duration-${duration.value}`}>
                {duration.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <FieldMessage id="duration-error" message={errors.duration?.message as string} messageType={errors.duration ? 'error' : undefined} />
      </div>

      <div className="flex flex-col gap-2">
        <Checkbox
          id="agreement"
          checked={agreed}
          onCheckedChange={value => setValue('agreed', value, { shouldValidate: true })}
          label={step('agreement')}
          helpMessageType={errors.agreed ? 'error' : undefined}
          helpMessage={errors.agreed?.message as string}
          className="text-foreground"
          trackingProps={{
            'data-track-name': 'Confirmation',
            'data-track-helper': 'Confirmation',
          }}
        />
      </div>

      <div data-track-container="Actions">
        <Button onClick={handleConfirm} className="w-full" disabled={isConfirmDisabled} data-track-name="Confirm" data-testid="time-out-confirm-button">
          {takeBreakMutation.isPending ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : step('confirm')}
        </Button>
      </div>
    </div>
  );
}

DurationStep.validateAndUpdateData = async (formMethods: ReturnType<typeof useFormContext>) => formMethods.trigger();

export { DurationStep };
